import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useJobPushNotifications } from "@/lib/notifications";
import { subscribeToPush } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Zap, MapPin, Star, Ship, Wrench, TrendingUp, ChevronRight, Bell, ExternalLink, Sparkles, Trophy, Video, Upload, Trash2, UserCog, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_LABEL = {
  pending: { text: "Pendiente", cls: "bg-[#EFE9E1] text-[#0B1D3A]" },
  accepted: { text: "Aceptada", cls: "bg-emerald-600 text-white" },
  rejected: { text: "Rechazada", cls: "bg-gray-300 text-gray-700" },
  withdrawn: { text: "Retirada", cls: "bg-gray-300 text-gray-700" },
};

export default function ProDashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [feed, setFeed] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [services, setServices] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [notifOn, setNotifOn] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [blockedDays, setBlockedDays] = useState(user?.professional?.blocked_days || []);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });

  const isBlocked = (iso) => blockedDays.includes(iso);
  const toggleBlockedDay = (iso) => {
    setBlockedDays(prev => prev.includes(iso) ? prev.filter(d => d !== iso) : [...prev, iso].sort());
  };
  const saveAvailability = async () => {
    try {
      await api.post("/professionals/me/availability", { blocked_days: blockedDays });
      toast.success("Disponibilidad guardada");
      setCalOpen(false);
      refresh && refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const buildCalendar = () => {
    const y = calMonth.getFullYear(); const m = calMonth.getMonth();
    const firstDay = new Date(y, m, 1).getDay(); // 0=Sun
    const offset = (firstDay + 6) % 7; // convert to Mon=0
    const total = daysInMonth(y, m);
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= total; d++) {
      const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push(iso);
    }
    return cells;
  };
  const monthName = calMonth.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  const prof = user?.professional || {};
  const videoUrl = prof.video_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${prof.video_path}` : null;
  const now = new Date().toISOString();
  const isPromoted = (prof.promoted_until || "") > now;
  const isFeatured = (prof.featured_until || "") > now;
  useJobPushNotifications(notifOn);

  const openEdit = () => {
    // Normalise legacy singular fields
    const legacyMode = prof.coverage_mode === "comarca" ? "comarcas" : (prof.coverage_mode || "all");
    setEdit({
      services: [...(prof.services || [])],
      coverage_mode: legacyMode,
      coverage_provinces: [...(prof.coverage_provinces || (prof.coverage_province ? [prof.coverage_province] : []))],
      coverage_comarcas: [...(prof.coverage_comarcas || (prof.coverage_comarca ? [prof.coverage_comarca] : []))],
      coverage_ports: [...(prof.coverage_ports || [])],
      bio: prof.bio || "",
    });
    setEditOpen(true);
  };

  const toggle = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  const saveEdit = async () => {
    if (!edit.services.length) { toast.error("Elige al menos un servicio"); return; }
    if (edit.coverage_mode !== "all" && edit.coverage_provinces.length === 0) {
      toast.error("Elige al menos una provincia"); return;
    }
    setSaving(true);
    try {
      await api.patch("/professionals/me", edit);
      toast.success("Perfil actualizado");
      setEditOpen(false);
      await (refresh && refresh());
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const editCurrentProvs = provinces.filter(p => edit?.coverage_provinces?.includes(p.name)) || [];
  const editAvailComarcas = [...new Set(editCurrentProvs.flatMap(p => p.comarcas || []))];
  const editAvailPorts = [...new Set(editCurrentProvs.flatMap(p => (p.ports || []).map(pt => typeof pt === "string" ? pt : pt.name)))];

  const enableNotifs = async () => {
    const ok = await subscribeToPush();
    if (ok) setNotifOn(true);
  };

  const buyPromotion = async (plan) => {
    try {
      const { data } = await api.post("/promotions/checkout", { plan, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error("Máx. 50 MB"); return; }
    // client-side duration guard (~30s)
    try {
      const dur = await new Promise((resolve, reject) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => resolve(v.duration);
        v.onerror = () => reject(new Error("bad_video"));
        v.src = URL.createObjectURL(file);
      });
      if (dur > 35) { toast.error(`Máx 30s (tu vídeo dura ${Math.round(dur)}s)`); return; }
    } catch { /* ignore, backend will still accept common formats */ }
    setVideoUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/professionals/me/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVideoPreview(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("Vídeo subido");
      refresh && refresh();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setVideoUploading(false); }
  };

  const removeVideo = async () => {
    if (!confirm("¿Quitar el vídeo de tu perfil?")) return;
    try {
      await api.delete("/professionals/me/video");
      setVideoPreview(null);
      toast.success("Vídeo eliminado");
      refresh && refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const load = async () => {
    try {
      const [f, o] = await Promise.all([api.get("/feed"), api.get("/offers/mine")]);
      setFeed(f.data); setMyOffers(o.data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    load();
    api.get("/services").then(r => setServices(r.data));
    api.get("/geo/provinces").then(r => setProvinces(r.data)).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const svcName = (id) => services.find(s => s.id === id)?.name || id;

  const bannerDismissed = typeof window !== "undefined" && localStorage.getItem("mh_onb_dismissed") === "1";
  const [showBanner, setShowBanner] = useState(!bannerDismissed);
  const onbDone = { services: (prof.services || []).length > 0, profile: (prof.bio || "").length >= 30, payouts: !!prof.stripe_payouts_enabled, kyc: prof.kyc_status === "verified" };
  const onbPercent = Math.round((Object.values(onbDone).filter(Boolean).length / 4) * 100);
  const missingCount = Object.values(onbDone).filter(v => !v).length;
  const dismissBanner = () => { localStorage.setItem("mh_onb_dismissed", "1"); setShowBanner(false); };

  const active = myOffers.filter(o => o.status === "accepted" && ["accepted", "in_progress"].includes(o.request?.status));
  const completedCount = myOffers.filter(o => o.request?.status === "completed").length;

  return (
    <div className="min-h-screen hub-cream">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="grid md:grid-cols-12 gap-10 mb-10">
          <div className="md:col-span-7">
            <div className="hub-pill hub-sand text-[#0B1D3A] mb-3"><Wrench size={12}/> Panel profesional</div>
            <h1 className="font-serif text-5xl text-[#0B1D3A] tracking-tight">Hola, {user?.name?.split(" ")[0]}.</h1>
            <p className="text-[#5C6B7D] mt-2">Estas son las solicitudes que <span className="italic">encajan contigo</span>. Sin buscarlas.</p>
            {showBanner && onbPercent < 100 && (
              <div className="hub-card p-4 mt-4 bg-gradient-to-r from-[#0B1D3A]/5 to-[#FF6B57]/5 border-[#FF6B57]/20" data-testid="onb-banner">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="font-display text-[10px] uppercase tracking-widest text-[#5C6B7D]">Tu perfil</span>
                      <span className="font-display text-xs font-semibold text-[#FF6B57]">{onbPercent}% completo</span>
                    </div>
                    <div className="h-1.5 bg-[#EFE9E1] rounded-full overflow-hidden mb-1.5">
                      <div className="h-full bg-gradient-to-r from-[#0B1D3A] to-[#FF6B57] transition-all" style={{ width: `${onbPercent}%` }} />
                    </div>
                    <p className="text-[11px] text-[#5C6B7D]">Ya recibes trabajos. Te faltan <b>{missingCount}</b> {missingCount === 1 ? "paso" : "pasos"} para ganar +60% de ofertas.</p>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" onClick={() => nav("/pro/onboarding")} className="hub-btn-primary rounded-full gap-1 text-xs" data-testid="onb-banner-cta">
                      Completar <ChevronRight size={12}/>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={dismissBanner} className="text-[#5C6B7D] text-[11px] rounded-full" data-testid="onb-banner-dismiss">Ocultar</Button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={enableNotifs} variant="ghost" className="rounded-full gap-2 text-sm border border-[#0B1D3A]/15" data-testid="enable-notifs-btn">
                <Bell size={14}/> {notifOn ? "Notificaciones activas" : "Activar avisos de trabajos urgentes"}
              </Button>
              <Button onClick={() => nav(`/pro/profile/${user.id}`)} variant="ghost" className="rounded-full gap-2 text-sm border border-[#0B1D3A]/15" data-testid="my-public-profile-btn">
                <ExternalLink size={14}/> Ver mi perfil público
              </Button>
              <Button onClick={openEdit} variant="ghost" className="rounded-full gap-2 text-sm border border-[#0B1D3A]/15" data-testid="edit-profile-btn">
                <UserCog size={14}/> Editar perfil
              </Button>
              <Button onClick={() => nav(`/pro/profile/${user?.id}`)} variant="ghost" className="rounded-full gap-2 text-sm border border-[#0B1D3A]/15" data-testid="view-public-profile-btn">
                <ExternalLink size={14}/> Ver mi perfil público
              </Button>
              <Dialog open={calOpen} onOpenChange={setCalOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="rounded-full gap-2 text-sm border border-[#0B1D3A]/15" data-testid="availability-btn">
                    <CalendarIcon size={14}/> Disponibilidad ({(prof.blocked_days || []).length} bloqueados)
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle className="font-serif text-3xl">Mi disponibilidad</DialogTitle></DialogHeader>
                  <p className="text-sm text-[#5C6B7D]">Toca los días en los que <b>NO</b> puedes trabajar. Los propietarios los verán en gris al pedirte cita.</p>
                  <div className="flex items-center justify-between mt-3">
                    <button onClick={() => { const d = new Date(calMonth); d.setMonth(d.getMonth() - 1); setCalMonth(d); }} className="p-2 rounded-full hover:bg-[#EFE9E1]" data-testid="cal-prev">‹</button>
                    <div className="font-display capitalize text-sm">{monthName}</div>
                    <button onClick={() => { const d = new Date(calMonth); d.setMonth(d.getMonth() + 1); setCalMonth(d); }} className="p-2 rounded-full hover:bg-[#EFE9E1]" data-testid="cal-next">›</button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mt-2 text-[10px] text-[#5C6B7D] uppercase font-display">
                    {["L","M","X","J","V","S","D"].map(d => <div key={d} className="text-center">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1 mt-1">
                    {buildCalendar().map((iso, idx) => {
                      if (!iso) return <div key={`e-${idx}`}/>;
                      const day = parseInt(iso.split("-")[2]);
                      const blocked = isBlocked(iso);
                      const past = new Date(iso) < new Date(new Date().toDateString());
                      return (
                        <button key={iso} disabled={past} onClick={() => toggleBlockedDay(iso)}
                          data-testid={`cal-day-${iso}`}
                          className={`h-9 rounded-md text-sm font-display transition-colors ${past ? "bg-transparent text-[#5C6B7D]/30" : blocked ? "bg-red-100 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}>
                          {day}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-xs text-[#5C6B7D]">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200 inline-block"/> Disponible</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-200 inline-block"/> Bloqueado</span>
                  </div>
                  <Button onClick={saveAvailability} className="hub-btn-primary w-full h-11 mt-3 rounded-full" data-testid="cal-save-btn">
                    Guardar disponibilidad
                  </Button>
                </DialogContent>
              </Dialog>
              <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="rounded-full gap-2 text-sm border border-[#0B1D3A]/15" data-testid="video-btn">
                    <Video size={14}/> {videoUrl ? "Cambiar vídeo (30s)" : "Añadir vídeo 30s"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle className="font-serif text-3xl">Vídeo de presentación</DialogTitle></DialogHeader>
                  <p className="text-sm text-[#5C6B7D]">Máx. 30 segundos y 50 MB. Formatos MP4, MOV o WEBM. Aparecerá en tu perfil público para que los propietarios te conozcan de un vistazo.</p>
                  {(videoPreview || videoUrl) && (
                    <video src={videoPreview || videoUrl} controls playsInline preload="metadata"
                      className="w-full rounded-2xl border border-[#0B1D3A]/10 mt-3" data-testid="video-preview"/>
                  )}
                  <div className="flex gap-2 mt-4">
                    <label className="hub-btn-primary rounded-full px-5 py-2.5 text-sm cursor-pointer inline-flex items-center gap-2" data-testid="video-upload-label">
                      <Upload size={14}/> {videoUploading ? "Subiendo..." : (videoUrl ? "Reemplazar" : "Subir vídeo")}
                      <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden"
                        onChange={(e) => uploadVideo(e.target.files?.[0])} disabled={videoUploading} data-testid="video-upload-input"/>
                    </label>
                    {videoUrl && (
                      <Button variant="ghost" onClick={removeVideo} className="text-red-500 rounded-full gap-2" data-testid="video-remove-btn">
                        <Trash2 size={14}/> Quitar
                      </Button>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={promoOpen} onOpenChange={setPromoOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="rounded-full gap-2 text-sm border-2 border-[#FF6B57] text-[#FF6B57]" data-testid="promote-btn">
                    <Sparkles size={14}/> {isFeatured ? "Perfil destacado ✓" : isPromoted ? "Boost activo ✓" : "Promocionar mi perfil"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle className="font-serif text-3xl">Aparece antes que nadie</DialogTitle></DialogHeader>
                  <p className="text-sm text-[#5C6B7D]">Elige un plan y tu perfil se destacará durante 30 días. Se paga con tarjeta y va directamente a MarinaHub.</p>
                  <div className="grid grid-cols-1 gap-3 mt-4">
                    <div className="hub-card p-5 border-2 hover:border-[#FF6B57] transition-colors cursor-pointer" onClick={() => buyPromotion("boost_30d")} data-testid="plan-boost">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-serif text-2xl">Boost Feed <span className="text-sm text-[#5C6B7D]">30 días</span></div>
                          <div className="text-sm text-[#5C6B7D] mt-1">Tus ofertas aparecen antes en el feed de los clientes de tu zona</div>
                        </div>
                        <div className="font-serif text-3xl hub-coral">29€</div>
                      </div>
                    </div>
                    <div className="hub-card p-5 border-2 hover:border-[#0B1D3A] transition-colors cursor-pointer relative" onClick={() => buyPromotion("featured_profile_30d")} data-testid="plan-featured">
                      <span className="absolute -top-2 right-4 hub-pill bg-[#0B1D3A] text-white"><Trophy size={11}/> El más elegido</span>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-serif text-2xl">Perfil Destacado <span className="text-sm text-[#5C6B7D]">30 días</span></div>
                          <div className="text-sm text-[#5C6B7D] mt-1">Boost feed + apareces en la sección "Profesionales cerca" del propietario</div>
                        </div>
                        <div className="font-serif text-3xl hub-coral">49€</div>
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] text-[#5C6B7D] italic mt-3">Pago único, no reembolsable una vez activado. Comisiones Stripe (1.4% + 0.25€) incluidas.</p>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-3 gap-3">
            <div className="hub-card p-4"><div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display">Rating</div><div className="font-serif text-3xl mt-1 flex items-center gap-1">{prof.rating?.toFixed(1) || "—"} <Star size={16} className="fill-[#FF6B57] text-[#FF6B57]"/></div></div>
            <div className="hub-card p-4"><div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display">Trabajos</div><div className="font-serif text-3xl mt-1">{prof.jobs_completed || 0}</div></div>
            <div className="hub-card p-4"><div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display">Activos</div><div className="font-serif text-3xl mt-1 hub-coral">{active.length}</div></div>
          </div>
        </div>

        <Tabs defaultValue="feed" className="w-full">
          <TabsList className="mb-6 bg-white/60 border border-[#0B1D3A]/10 rounded-full p-1">
            <TabsTrigger value="feed" data-testid="tab-feed" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Feed inteligente ({feed.filter(f => !f.already_offered).length})</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Mis ofertas ({myOffers.length})</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Trabajos activos ({active.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="feed">
            <div className="grid md:grid-cols-2 gap-4">
              {feed.length === 0 && <div className="hub-card p-10 text-center text-[#5C6B7D]">No hay solicitudes en tu zona ahora mismo. Vuelve pronto — llegan cada día.</div>}
              {feed.map((r) => (
                <button key={r.id} onClick={() => nav(`/request/${r.id}`)} data-testid={`feed-card-${r.id}`}
                  className="hub-card p-6 text-left w-full group">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {r.urgent && <span className="hub-pill bg-[#FF6B57] text-white"><Zap size={11}/> Urgente</span>}
                        {r.already_offered && <span className="hub-pill bg-[#EFE9E1] text-[#0B1D3A]">Ofertado</span>}
                        <span className="hub-pill hub-sand text-[#0B1D3A]">{svcName(r.service_type)}</span>
                      </div>
                      <div className="font-serif text-xl mt-2">{r.boat_name || "Barco sin nombre"} · {r.boat_size}</div>
                      <div className="text-sm text-[#5C6B7D] mt-1 flex items-center gap-1"><MapPin size={12}/> {r.port}, {r.comarca || r.province}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display">Match</div>
                      <div className="font-serif text-2xl hub-coral">{Math.min(100, Math.round(r.match_score || 0))}</div>
                    </div>
                  </div>
                  <div className="match-bar mt-2"><span style={{ width: `${Math.min(100, r.match_score || 0)}%` }}/></div>
                  {r.notes && <p className="text-sm text-[#0B1D3A]/80 mt-4 line-clamp-2">{r.notes}</p>}
                  <div className="flex items-center justify-end gap-1 mt-4 text-[#FF6B57] font-display text-sm">
                    Ver y ofertar <ChevronRight size={16}/>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="offers">
            <div className="space-y-3">
              {myOffers.length === 0 && <div className="hub-card p-10 text-center text-[#5C6B7D]">Aún no has enviado ninguna oferta.</div>}
              {myOffers.map((o) => {
                const st = STATUS_LABEL[o.status] || STATUS_LABEL.pending;
                return (
                  <button key={o.id} onClick={() => nav(`/request/${o.request_id}`)} data-testid={`offer-${o.id}`}
                    className="hub-card p-5 w-full text-left flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap"><span className={`hub-pill ${st.cls}`}>{st.text}</span></div>
                      <div className="font-serif text-lg mt-1">{svcName(o.request?.service_type)} · {o.request?.port}</div>
                      <div className="text-sm text-[#5C6B7D]">{o.price}€ · {o.duration_hours}h</div>
                    </div>
                    <ChevronRight className="text-[#5C6B7D]"/>
                  </button>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="active">
            <div className="space-y-3">
              {active.length === 0 && <div className="hub-card p-10 text-center text-[#5C6B7D]">No tienes trabajos activos ahora mismo.</div>}
              {active.map((o) => (
                <button key={o.id} onClick={() => nav(`/request/${o.request_id}`)} data-testid={`active-${o.id}`}
                  className="hub-card p-5 w-full text-left flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#FF6B57]/10 text-[#FF6B57] flex items-center justify-center"><Ship size={16}/></div>
                  <div className="flex-1">
                    <div className="font-serif text-lg">{svcName(o.request?.service_type)} · {o.request?.port}</div>
                    <div className="text-sm text-[#5C6B7D]">Cliente: {o.request?.owner_name}</div>
                  </div>
                  <div className="font-serif text-xl hub-coral">{o.price}€</div>
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-3xl">Editar mi perfil</DialogTitle></DialogHeader>
            {edit && (
              <div className="space-y-5 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Servicios que ofreces</Label>
                  <div className="mt-2 flex flex-wrap gap-2 max-h-48 overflow-y-auto p-3 rounded-lg border border-[#0B1D3A]/15">
                    {services.map(s => {
                      const on = edit.services.includes(s.id);
                      return (
                        <button key={s.id} type="button" data-testid={`edit-svc-${s.id}`}
                          onClick={() => setEdit({ ...edit, services: toggle(edit.services, s.id) })}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Cobertura</Label>
                  <div className="mt-2 grid grid-cols-4 gap-1 p-1 bg-white border border-[#0B1D3A]/15 rounded-full">
                    {[{k:"all",l:"Toda España"},{k:"provinces",l:"Provincias"},{k:"comarcas",l:"Comarcas"},{k:"ports",l:"Puertos"}].map(({k,l}) => (
                      <button key={k} type="button"
                        onClick={() => setEdit({ ...edit, coverage_mode: k, coverage_provinces: k === "all" ? [] : edit.coverage_provinces, coverage_comarcas: k === "comarcas" ? edit.coverage_comarcas : [], coverage_ports: k === "ports" ? edit.coverage_ports : [] })}
                        data-testid={`edit-cov-${k}`}
                        className={`text-xs py-2 rounded-full font-display font-semibold transition-colors ${edit.coverage_mode === k ? "bg-[#0B1D3A] text-white" : "text-[#0B1D3A]"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                {edit.coverage_mode !== "all" && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">
                      {edit.coverage_mode === "provinces" ? "Elige provincias" : "1. Elige provincia(s) primero"}
                    </Label>
                    <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto p-3 rounded-lg border border-[#0B1D3A]/15">
                      {provinces.map(p => {
                        const on = edit.coverage_provinces.includes(p.name);
                        return (
                          <button key={p.name} type="button" data-testid={`edit-prov-${p.name}`}
                            onClick={() => setEdit({ ...edit, coverage_provinces: toggle(edit.coverage_provinces, p.name) })}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {edit.coverage_mode === "comarcas" && edit.coverage_provinces.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">2. Elige comarcas</Label>
                    <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto p-3 rounded-lg border border-[#0B1D3A]/15">
                      {editAvailComarcas.map(c => {
                        const on = edit.coverage_comarcas.includes(c);
                        return (
                          <button key={c} type="button" data-testid={`edit-com-${c}`}
                            onClick={() => setEdit({ ...edit, coverage_comarcas: toggle(edit.coverage_comarcas, c) })}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#FF6B57] text-white border-[#FF6B57]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {edit.coverage_mode === "ports" && edit.coverage_provinces.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">2. Elige puertos</Label>
                    <div className="mt-2 flex flex-wrap gap-2 max-h-40 overflow-y-auto p-3 rounded-lg border border-[#0B1D3A]/15">
                      {editAvailPorts.map(p => {
                        const on = edit.coverage_ports.includes(p);
                        return (
                          <button key={p} type="button" data-testid={`edit-port-${p}`}
                            onClick={() => setEdit({ ...edit, coverage_ports: toggle(edit.coverage_ports, p) })}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#FF6B57] text-white border-[#FF6B57]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                            {p}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Sobre ti</Label>
                  <Textarea value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} placeholder="Años de experiencia, especialidad, certificados..." className="mt-1.5" data-testid="edit-bio"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Foto de perfil</Label>
                  <p className="text-xs text-[#5C6B7D] mt-1">Los clientes confían más en un perfil con cara. Cuadrada, mín 400×400.</p>
                  <div className="mt-2 flex items-center gap-3">
                    {prof.photo_path ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${prof.photo_path}`} alt="Foto perfil" className="w-16 h-16 rounded-full object-cover border border-[#0B1D3A]/10"/>
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#EFE9E1] flex items-center justify-center font-serif text-2xl text-[#0B1D3A]">{user?.name?.[0] || "?"}</div>
                    )}
                    <label className="hub-btn-primary rounded-full px-4 py-2 text-xs cursor-pointer inline-flex items-center gap-1.5" data-testid="edit-photo-label">
                      <Upload size={12}/> {prof.photo_path ? "Cambiar" : "Subir foto"}
                      <input type="file" accept="image/*" className="hidden" data-testid="edit-photo-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          if (file.size > 10*1024*1024) { toast.error("Máx 10 MB"); return; }
                          const fd = new FormData(); fd.append("file", file);
                          try {
                            await api.post("/users/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                            toast.success("Foto actualizada");
                            await (refresh && refresh());
                          } catch (err) { toast.error(apiErr(err)); }
                        }}/>
                    </label>
                    {prof.photo_path && (
                      <Button variant="ghost" size="sm" className="text-red-500 text-xs rounded-full" data-testid="edit-photo-remove"
                        onClick={async () => { try { await api.delete("/users/me/photo"); toast.success("Foto quitada"); await (refresh && refresh()); } catch(e){ toast.error(apiErr(e)); } }}>
                        <Trash2 size={12}/> Quitar
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Galería de trabajos</Label>
                  <p className="text-xs text-[#5C6B7D] mt-1">Fotos de trabajos anteriores (máx 12). Se ven en tu perfil público.</p>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {(prof.gallery || []).map(p => (
                      <div key={p} className="relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-20 object-cover rounded-lg border border-[#0B1D3A]/10"/>
                        <button className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`gallery-remove-${p}`}
                          onClick={async () => { try { await api.delete(`/professionals/me/gallery?path=${encodeURIComponent(p)}`); await (refresh && refresh()); } catch(e){ toast.error(apiErr(e)); } }}>✕</button>
                      </div>
                    ))}
                    {(prof.gallery || []).length < 12 && (
                      <label className="w-full h-20 rounded-lg border-2 border-dashed border-[#0B1D3A]/20 flex items-center justify-center cursor-pointer hover:bg-[#FBF9F5]" data-testid="gallery-add-label">
                        <Upload size={16} className="text-[#5C6B7D]"/>
                        <input type="file" accept="image/*" className="hidden" data-testid="gallery-add-input"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return;
                            if (file.size > 10*1024*1024) { toast.error("Máx 10 MB"); return; }
                            const fd = new FormData(); fd.append("file", file);
                            try {
                              await api.post("/professionals/me/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
                              toast.success("Foto añadida");
                              await (refresh && refresh());
                            } catch (err) { toast.error(apiErr(err)); }
                          }}/>
                      </label>
                    )}
                  </div>
                </div>
                <Button onClick={saveEdit} disabled={saving} className="hub-btn-primary w-full h-11" data-testid="edit-save-btn">
                  {saving ? "Guardando..." : "Guardar cambios"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
